import { BillingMethods } from '../constantsv2';

import { analytics } from './analytics/analytics';

interface BaseEcommerceEvent {
  event?: string;
  ecommerce?: Record<string, unknown> | null;
  gaEventCategory?: string;
  gaEventAction?: string;
  gaEventLabel?: string;
  gaEventValue?: number;
  gaEventNonInteractionBool?: boolean;
}

interface CartStateSnapshot {
  amounts: string;
  skus: string;
  quantities: string;
}
interface ParsedKitItem {
  index: number;
  item_id: string;
  item_name: string;
  currency: string;
  price: number;
  coupon?: string;
  discount: number;
  item_brand: '23andMe';
  item_category: 'DNA Kits';
  quantity: 1;
}

type EventCategory =
  | 'store_upgrade_subscription_payment_page'
  | 'store_subscription_payment_page'
  | 'store_subscription_renewal_payment_page'
  | 'cancelation_flow'
  | 'store_subscription';

// GA categories mirrored to Customer.io as `gaEvent`, the shape dbt's GA event tables read.
const CIO_FORWARDED_GA_CATEGORIES = new Set<string>(['cancelation_flow']);

let cioProfileId: string | undefined;
let cioAccountId: string | undefined;

export function setCancellationFlowProfile({
  profileId,
  accountId,
}: {
  profileId?: string;
  accountId?: IAccount['accountId'];
}) {
  cioProfileId = profileId || undefined;
  cioAccountId = accountId || undefined;
}

function forwardGaEventToCustomerIo(payload: BaseEcommerceEvent) {
  const { event, gaEventCategory } = payload;
  if (
    event !== 'gaEvent' ||
    !gaEventCategory ||
    !CIO_FORWARDED_GA_CATEGORIES.has(gaEventCategory)
  ) {
    return;
  }
  analytics.track('gaEvent', {
    ...payload,
    ...(cioAccountId ? { account_id: cioAccountId } : {}),
    ...(cioProfileId ? { profile_id: cioProfileId } : {}),
  });
}

export function buttonClickGa(category: EventCategory, label: string) {
  GaEventHandler.pushToDataLayer({
    event: 'gaEvent',
    gaEventCategory: category,
    gaEventAction: 'button_click',
    gaEventLabel: label,
    gaEventValue: 0,
    gaEventNonInteractionBool: false,
  });
}

export function cardClickGa(category: EventCategory, label: string) {
  GaEventHandler.pushToDataLayer({
    event: 'gaEvent',
    gaEventCategory: category,
    gaEventAction: 'card_click',
    gaEventLabel: label,
    gaEventValue: 0,
    gaEventNonInteractionBool: false,
  });
}

export function pageViewGa(category: EventCategory, label: string) {
  GaEventHandler.pushToDataLayer({
    event: 'gaEvent',
    gaEventCategory: category,
    gaEventAction: 'page_view',
    gaEventLabel: label,
    gaEventValue: 0,
    gaEventNonInteractionBool: false,
  });
}

class GaEventHandler {
  static GTM_EVENT_ADD_SHIPPING_INFO = 'add_shipping_info';
  static GTM_EVENT_ADD_PAYMENT_INFO = 'add_payment_info';
  static GTM_EVENT_ADD_TO_CART = 'add_to_cart';
  static GTM_EVENT_REMOVE_FROM_CART = 'remove_from_cart';
  static GTM_EVENT_UPGRADE_IN_CART = 'upgrade_in_cart';
  static GTM_EVENT_DOWNGRADE_IN_CART = 'downgrade_in_cart';
  static GTM_EVENT_TRACK_TOTAL_HEALTH = 'track_th';
  static GTM_EVENT_TOTAL_HEALTH_UPGRADE = 'checkout_total_health';
  static GTM_EVENT_CHECKOUT_UPGRADE_SUBSCRIPTION = 'checkout_upgrade_subscription';
  static GTM_EVENT_CHECKOUT_SUBSCRIPTION = 'checkout_subscription';

  // Alias → GTM tracker label map. Any new CartV2 product alias that should emit
  // a quantity-change AnalyticsEvent must be added here.
  private static readonly QUANTITY_CHANGE_TRACKERS: Record<string, string> = {
    health: 'health_ancestry',
    ancestry: 'ancestry',
    prepaid: 'health_ancestry',
  };

  static pushToDataLayer(eventDetails: BaseEcommerceEvent = { event: '', ecommerce: {} }): void {
    forwardGaEventToCustomerIo(eventDetails);
    if (!window.dataLayer) {
      return;
    }
    window.dataLayer.push(eventDetails);
  }

  static trackNonEcommerceGaEvent(
    category: string,
    action: string,
    label: string,
    interactive: boolean = false,
  ) {
    // Non ecommerce GA events need to have an event of 'gaEvent' to fire a network call. NonInteractionBool should be true for non-interactive events.
    const payload = {
      event: 'gaEvent',
      gaEventCategory: category,
      gaEventAction: action,
      gaEventLabel: label,
      gaEventValue: 0,
      gaEventNonInteractionBool: !interactive,
    };
    this.trackEvent(payload);
  }

  static parseKit(currency: string, kit: Kit, index: number): ParsedKitItem {
    return {
      index: index,
      // BKO-5384: gaItemId is the product tracking id, or item_id for a SKU with none, decided
      // server-side. Falls back so a cart response predating the field still emits an id.
      item_id: String(kit.gaItemId ?? kit.itemId),
      item_name: kit.itemName,
      currency: currency,
      price: kit.discountPrice,
      coupon: kit.discountProgram,
      discount: kit.discount,
      item_brand: '23andMe',
      item_category: 'DNA Kits',
      quantity: 1,
    };
  }

  static parseSubscription(subscriptionInfo: SubscriptionInfo): ParsedKitItem {
    const hasDiscount = subscriptionInfo.discountAmountNumber > 0;
    const discountedPrice = hasDiscount
      ? subscriptionInfo.discountedPriceNumber
      : subscriptionInfo.priceNumber;
    return {
      index: 0,
      // BKO-5384: gaItemId is the tier's tracking id, decided server-side. Falls back to the
      // plan-<pk> form this always sent.
      item_id: subscriptionInfo.gaItemId ?? 'plan-' + subscriptionInfo.storePlanId,
      item_name: subscriptionInfo.planName,
      currency: subscriptionInfo.currency,
      price: discountedPrice,
      coupon: subscriptionInfo.coupon,
      discount: subscriptionInfo.discountAmountNumber,
      item_brand: '23andMe',
      item_category: 'DNA Kits',
      quantity: 1,
    };
  }

  static trackEvent(payload: BaseEcommerceEvent, gtmNullPayload = false): void {
    window.dataLayer = window.dataLayer || []; // Initialize dataLayer if it doesn't exist.
    if (gtmNullPayload) {
      window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
    }
    window.dataLayer.push(payload);
    forwardGaEventToCustomerIo(payload);
  }

  static addToCart(cart: Cart, gtmNullPayload?: boolean): void {
    this.trackCartEcommerce(this.GTM_EVENT_ADD_TO_CART, cart, gtmNullPayload);
  }

  static removeFromCart(cart: Cart, gtmNullPayload?: boolean): void {
    this.trackCartEcommerce(this.GTM_EVENT_REMOVE_FROM_CART, cart, gtmNullPayload);
  }

  static upgradeInCart(cart: Cart, gtmNullPayload?: boolean): void {
    this.trackCartEcommerce(this.GTM_EVENT_UPGRADE_IN_CART, cart, gtmNullPayload);
  }

  static downgradeInCart(cart: Cart, gtmNullPayload?: boolean): void {
    this.trackCartEcommerce(this.GTM_EVENT_DOWNGRADE_IN_CART, cart, gtmNullPayload);
  }

  static pushCartState(cart: Cart): void {
    window.dataLayer = window.dataLayer || [];
    const snapshot: CartStateSnapshot = {
      amounts: cart.kits.map((kit) => kit.discountPrice).join('|'),
      skus: cart.kits.map((kit) => kit.sku).join('|'),
      // CartV2 kits are always qty 1; KitSerializer does not expose qty to the frontend.
      quantities: cart.kits.map(() => 1).join('|'),
    };
    (window.dataLayer as (BaseEcommerceEvent | CartStateSnapshot)[]).push(snapshot);
  }

  static trackQuantityChange(alias: string, action: 'add' | 'delete'): void {
    const tracker = this.QUANTITY_CHANGE_TRACKERS[alias];
    if (!tracker) return;
    this.pushToDataLayer({
      event: 'AnalyticsEvent',
      eventCategory: 'store',
      eventAction: 'quantity change click',
      eventLabel: `${tracker}_${action}`,
    } as BaseEcommerceEvent);
  }

  private static trackCartEcommerce(eventName: string, cart: Cart, gtmNullPayload?: boolean): void {
    const payload = {
      event: eventName,
      ecommerce: {
        currency: cart.currency,
        value: cart.totals.itemsTotal,
        items: cart.kits.map((item, index) => this.parseKit(cart.currency, item, index)),
      },
    };
    this.trackEvent(payload, gtmNullPayload);
  }

  static addShippingInfo(data: Cart, shipMethod: ShipMethod, gtmNullPayload?: boolean): void {
    const payload = {
      event: this.GTM_EVENT_ADD_SHIPPING_INFO,
      ecommerce: {
        currency: data.currency,
        value: data.totals.itemsTotal,
        shippingTier: shipMethod.code,
        items: data.kits.map((item, index) => this.parseKit(data.currency, item, index)),
      },
    };
    this.trackEvent(payload, gtmNullPayload);
  }

  static paymentMethod(data: SavedPayment): BillingMethods {
    let method: BillingMethods = BillingMethods.CREDIT_CARD;
    if (data.isApplepay) {
      method = BillingMethods.APPLEPAY;
    } else if (data.isPaypalAccount) {
      method = BillingMethods.PAYPAL;
    }
    return method;
  }

  static addPaymentInfo(data: Cart, billing: Billing, gtmNullPayload?: boolean): void {
    let method = billing.method;
    if (method === BillingMethods.SAVED_PAYMENT) {
      // TODO - why are saved payment flags tucked away in customAddress?
      // @ts-ignore
      method = this.paymentMethod(billing.customAddress);
    }
    const payload = {
      event: this.GTM_EVENT_ADD_PAYMENT_INFO,
      ecommerce: {
        currency: data.currency,
        value: data.totals.itemsTotal,
        paymentType: method,
        items: data.kits.map((item, index) => this.parseKit(data.currency, item, index)),
      },
    };
    this.trackEvent(payload, gtmNullPayload);
  }

  static addPaymentInfoSubs(
    subscriptionInfo: SubscriptionInfo,
    savedPayment: SavedPayment,
    kit: Kit,
    gtmNullPayload?: boolean,
  ): void {
    const method = this.paymentMethod(savedPayment);
    const items = [this.parseSubscription(subscriptionInfo)];
    let value = subscriptionInfo.priceNumber;
    if (subscriptionInfo.isHealthUpgrade) {
      items.push(this.parseKit(subscriptionInfo.currency, kit, 1));
      value = value + kit.discountPrice + kit.discount;
    }

    const payload = {
      event: this.GTM_EVENT_ADD_PAYMENT_INFO,
      ecommerce: {
        currency: subscriptionInfo.currency,
        value: value,
        paymentType: method,
        items: items,
      },
    };
    this.trackEvent(payload, gtmNullPayload);
  }

  static trackTotalHealth(identifier: string, totalKits: number, gtmNullPayload?: boolean): void {
    const payload = {
      event: this.GTM_EVENT_TRACK_TOTAL_HEALTH,
      ecommerce: {
        identifier: identifier,
        total_kits: totalKits,
      },
    };
    this.trackEvent(payload, gtmNullPayload);
  }

  static totalHealthAcceptSavePayment(alias: string, price: number): void {
    const payload = {
      event: this.GTM_EVENT_TOTAL_HEALTH_UPGRADE,
      ecommerce: {
        event_category: 'youdot_total_health_checkout',
        event_action: 'box_checked',
        event_label: 'payment_saved',
        alias: alias,
        price: price,
      },
    };
    this.trackEvent(payload);
  }

  static totalHealthAcceptTos(alias: string, price: number): void {
    const payload = {
      event: this.GTM_EVENT_TOTAL_HEALTH_UPGRADE,
      ecommerce: {
        event_category: 'youdot_total_health_checkout',
        event_action: 'box_checked',
        event_label: 'terms_of_service',
        alias: alias,
        price: price,
      },
    };
    this.trackEvent(payload);
  }

  static trackSubscriptionPaymentPageView(event_category: EventCategory, source?: string): void {
    source = source ? source : 'unknown';
    this.trackNonEcommerceGaEvent(event_category, 'page_view', source);
  }
}

// Window augmentation for dataLayer
declare global {
  interface Window {
    dataLayer: (BaseEcommerceEvent | CartStateSnapshot)[];
  }
}

export default GaEventHandler;
